import React, {useEffect, useState} from "react";
import {Form, Input, Switch, Table, Button, Radio} from "antd";
import {barcodeOptionTypes} from "./BarcodeOptionTypes";
import {useStore} from "../../../../hooks/useStore";
import {useTranslation} from "react-i18next";
import {FormikProvider, getIn, useFormik} from "formik";
import {ErrorFocus} from "../../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CompanyButtonContainer} from "../optionsStyles";
import {EmptyMessage} from "../../../_defaultComponents/EmptyMessage/EmptyMessage";

const Options = ({
    companyLoading,
    setCompanyLoading,
    companyData,
    setCompanyRecall,
    companyRecallRef
}: barcodeOptionTypes) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const barcode = companyData.data?.options?.barcode || {};
    const proofOfDelivery = companyData.data?.options.proof || {};

    const formik = useFormik({
        initialValues: {
            options: {barcode},
            applyBarcodeOptions: false,
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        onSubmit: async values => {
            try {
                setCompanyLoading(true);

                if (values.isActive) {
                    values.applyBarcodeOptions = true;
                }

                if (!!proofOfDelivery?.isActive && !!values?.options?.barcode?.isActive) {
                    stores.appStore.openConfirmModal({
                        title: t("modal.confirmTitle", {ns: "common"}),
                        content: t("other.proofOverrideConfirmContent", {
                            ns: "common"
                        }),
                        onOk: async () => {
                            await deactivateCompanyProofOfDeliveryOptions(values);
                            await updateCompanyBarcodeOptions(values);
                        },
                        onCancel: () => setCompanyLoading(false)
                    });
                } else {
                    await updateCompanyBarcodeOptions(values);
                }
            } catch (e) {
                console.log(e);
            }
        }
    });

    const deactivateCompanyProofOfDeliveryOptions = async values => {
        values.options.proof.isActive = false;
        await stores.companyStore.updateCompanyProofOfDelivery(values);
        return;
    };

    const updateCompanyBarcodeOptions = async values => {
        await stores.companyStore.updateCompanyBarcodeOptions(values);
        setCompanyFormData(values);
        setCompanyRecall(!companyRecallRef.current);
        setCompanyLoading(false);
        return;
    };

    const {values, errors, touched, handleSubmit, isSubmitting, setFieldValue} = formik;
    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    const renderSwitch = key => {
        return (
            <Table.Column
                title={`${t(`fields.barcodeOptions.${key}.label`, {
                    ns: "common"
                })} (${t(`fields.barcodeOptions.${key}.short`, {ns: "common"})})`}
                dataIndex="isActive"
                render={() => (
                    <Form.Item>
                        <Switch
                            checked={values.options.barcode && values.options.barcode[key]}
                            checkedChildren={t("other.active", {ns: "common"})}
                            unCheckedChildren={t("other.passive", {ns: "common"})}
                            data-testid={key}
                            onChange={e => {
                                setFieldValue(`options.barcode.${key}`, e);
                                setIsCompanyFormChanged(true);
                            }}
                        />
                    </Form.Item>
                )}
                align="center"
            />
        );
    };

    const renderInput = (key, disableKey) => {
        const getInErrors = getIn(errors, `options.barcode.${key}`);
        const getInTouch = getIn(touched, `options.barcode.${key}`);
        const hasError = getInErrors && getInTouch;
        return (
            <Table.Column
                title={`${t(`fields.barcodeOptions.${key}.label`, {
                    ns: "common"
                })} (${t(`fields.barcodeOptions.${key}.short`, {ns: "common"})})`}
                dataIndex="limit"
                render={() => (
                    <Form.Item
                        validateStatus={hasError ? "error" : "success"}
                        help={hasError ? getInErrors : null}
                    >
                        <Input
                            type="number"
                            name="limit"
                            data-testid="limitInput"
                            min={0}
                            value={
                                values.options.barcode && values.options.barcode[key]
                                    ? values.options.barcode[key]
                                    : barcode && barcode[key]
                            }
                            onChange={e =>
                                setFieldValue(`options.barcode.${key}`, e.target.value || "")
                            }
                            disabled={
                                !values.options.barcode || !values.options.barcode[disableKey]
                            }
                        />
                    </Form.Item>
                )}
                align="center"
            />
        );
    };

    return (
        <Form
            autoComplete="off"
            onFinish={handleSubmit}
            data-testid="barcodeOptionsForm"
            onChange={() => setIsCompanyFormChanged(true)}
        >
            <Table
                loading={companyLoading}
                dataSource={[companyData.data]}
                rowKey="_id"
                pagination={false}
                data-testid="barcodeOptionsTable"
                locale={{
                    emptyText: <EmptyMessage />
                }}
            >
                <ErrorFocus formik={formik} />
                <FormikProvider value={formik}>
                    {renderSwitch("isActive")}
                    <Table.Column
                        title={`${t("fields.barcodeOptions.requiredStep.label", {
                            ns: "common"
                        })} (${t("fields.barcodeOptions.requiredStep.short", {
                            ns: "common"
                        })})`}
                        dataIndex="requiredStep"
                        render={() => (
                            <Form.Item data-testid="requiredStepRadioGroup">
                                <Radio.Group
                                    onChange={e => {
                                        setFieldValue(
                                            "options.barcode.requiredStep",
                                            e.target.value
                                        );
                                        setIsCompanyFormChanged(true);
                                    }}
                                    value={values.options?.barcode?.requiredStep}
                                >
                                    <Radio value={"preTrip"} data-testid="requiredStepRadio">
                                        {t("other.pickup", {ns: "common"})}
                                    </Radio>
                                    <Radio value={"postTrip"} data-testid="requiredStepRadio">
                                        {t("other.delivery", {ns: "common"})}
                                    </Radio>
                                    <Radio value={"both"} data-testid="requiredStepRadio">
                                        {t("filters.all", {ns: "common"})}
                                    </Radio>
                                </Radio.Group>
                            </Form.Item>
                        )}
                        align="left"
                    />
                    <Table.Column
                        title={`${t("fields.barcodeOptions.requiredType.label", {
                            ns: "common"
                        })} (${t("fields.barcodeOptions.requiredType.short", {
                            ns: "common"
                        })})`}
                        dataIndex="requiredType"
                        render={() => (
                            <Form.Item data-testid="requiredTypeRadioGroup">
                                <Radio.Group
                                    onChange={e => {
                                        setFieldValue(
                                            "options.barcode.requiredType",
                                            e.target.value
                                        );
                                        setIsCompanyFormChanged(true);
                                    }}
                                    value={values.options?.barcode?.requiredType}
                                >
                                    <Radio value={"delivery"} data-testid="requiredTypeRadio">
                                        {t("deliveryTypes.delivery", {ns: "common"})}
                                    </Radio>
                                    <Radio value={"return"} data-testid="requiredTypeRadio">
                                        {t("deliveryTypes.return", {ns: "common"})}
                                    </Radio>
                                    <Radio value={"both"} data-testid="requiredTypeRadio">
                                        {t("filters.all", {ns: "common"})}
                                    </Radio>
                                </Radio.Group>
                            </Form.Item>
                        )}
                        align="left"
                    />
                </FormikProvider>
            </Table>
            <CompanyButtonContainer justify="end" align="middle">
                <Button
                    htmlType="submit"
                    disabled={!isCompanyFormChanged || isSubmitting}
                    type="primary"
                    data-testid="submitButton"
                >
                    {t("actions.save", {ns: "common"})}
                </Button>
            </CompanyButtonContainer>
        </Form>
    );
};

export default Options;
